"use client";

import { memo } from "react";
import { cn } from "@/lib/utils";
import {
  GOOGLE_REVIEW_URL,
  TRUSTPILOT_RATING_DISPLAY,
  TRUSTPILOT_REVIEW_URL,
} from "@/components/screen/start-order/components/Checkout/constants";

/* ============================================================================
 * TrustStrip — Component 8 (production drop-in)
 *
 * Source spec: docs / Claude design brief
 *   `quality-trust-section/component-8-cursor-brief.md`
 *
 * Single horizontal strip that lives directly under the home hero. Replaces
 * the old <PartnersLogos> mount on Home.tsx only. PartnersLogos itself is
 * untouched and still used by 11 other pages.
 *
 * Layout:
 *   3 customer wordmarks │ Trustpilot/Google review box │ 3 customer wordmarks
 *
 * Responsive (per brief):
 *   ≥1024px (lg+)         → 7-element horizontal row, ~80px tall, space-between
 *   768–1023px (md→lg)    → same horizontal layout, tighter padding/gaps
 *   <768px (sm and below) → review box on top, 6 logos in a 3-col grid below
 *
 * Tokens:
 *   strip bg     = bg-background-secondary  (#F7F7F9)
 *   card bg      = bg-white
 *   soft border  = border-gray-100         (#D7DADC)
 *   default brdr = border-neutral-200      (#BCC2C4)
 *   primary text = text-semantic-text-primary (#0B0E10)
 *   subtle text  = text-gray-500           (#58666C)
 *   Trustpilot green and Google G colors are external brand values, kept
 *   as arbitrary hex on purpose (not LanguexDS tokens).
 *
 * Wordmarks: all six are styled text by design (Imran's call — the real
 * brand logos in the asset library were low-quality and visually too
 * heavy, breaking the strip's compact rhythm). Future: someone may
 * source clean SVGs and re-A/B against the text treatment; until then
 * the wordmarks are the canonical look. The `<TrustLogoTile>` wrapper
 * is still designed to be swap-in friendly (drop an <img> in and the
 * opacity + hover behavior just applies) — but DON'T proactively swap
 * to SVGs without an explicit go-ahead from product.
 *
 * Trustpilot rating is read from the shared TRUSTPILOT_RATING_DISPLAY
 * constant so this strip can't drift out of sync with the Cost Calculator,
 * /get-quote confirmation, or the home JSON-LD. Review count is
 * intentionally omitted per brief ("dual-platform marks, no count").
 *
 * Click behavior: the two platform labels at the bottom of the box are
 * individual anchors — Trustpilot mark → TRUSTPILOT_REVIEW_URL, Google
 * mark → GOOGLE_REVIEW_URL. Respects Imran's global rule that every
 * Trustpilot mention on the site links back to the review profile, and
 * mirrors how the Quality-page LanguexExcellence box wires its platforms.
 * Outer card is not a link so the two platforms can route independently.
 * ========================================================================== */

const TrustpilotStar = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    className={className}
  >
    <polygon
      fill="currentColor"
      points="12,2 15,9 22,9.5 17,14.5 18.5,22 12,18 5.5,22 7,14.5 2,9.5 9,9"
    />
  </svg>
);

const GoogleGlyph = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    className={className}
  >
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

// Shared tile wrapper for each wordmark. Locks in the height, the
// muted/hover color treatment, and the shrink/flex behavior so swapping
// raw text marks for real <img>/SVG later is a drop-in change.
//
// 2026-05-25 bug fix: height was previously written as
// `h-[22px] sm:h-[26px] md:h-[22px]` — but Tailwind's `sm:` means
// ">=640px", NOT "mobile". The original cascade rendered 22px on actual
// phones (<640px), 26px on the sm-only band (640-767px), then 22px
// again on desktop — opposite of the brief, which wants 26px on real
// mobile. Rewritten to mobile-first: 26px base, 22px from md upwards.
const TrustLogoTile = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      "flex items-center justify-center shrink-0 text-gray-500 opacity-[0.78] transition-[opacity,color] duration-200 ease-out hover:opacity-100 hover:text-semantic-text-primary",
      "h-[26px] md:h-[22px]",
      className
    )}
  >
    {children}
  </div>
);

const TrustStrip = () => {
  const ratingLabel = `${TRUSTPILOT_RATING_DISPLAY} out of 5 stars`;
  return (
    // 2026-05-25 polish pass:
    //  - Bumped `lg:py-5` -> `lg:py-6` so the desktop strip lands closer
    //    to the brief's stated ~80px height target (was reading ~70px).
    //  - Earlier polish pass added a `border-b border-gray-100` hairline
    //    to close the strip against ServiceOfferings below; pulled after
    //    Imran's review — the soft color delta from #F7F7F9 to the next
    //    section's white was already doing the separation work, and the
    //    1px line created visual asymmetry with the seamless top edge
    //    (where the strip blends into the hero gradient). Soft-on-soft
    //    is the canonical look now; don't reintroduce the hairline
    //    without an explicit go-ahead.
    <section
      aria-label="Customer trust signals"
      className="section bg-background-secondary py-7 md:py-[18px] lg:py-6 antialiased"
    >
      <div className="container mx-auto">
        <div
          className={cn(
            // Mobile: stack column, box first, logo groups below as grids
            "flex flex-col items-center gap-6",
            // md+: 7-element horizontal row, space-between
            "md:flex-row md:items-center md:justify-between md:gap-5",
            "lg:gap-7"
          )}
        >
          {/* LEFT WORDMARKS (3) — order-2 on mobile so the box can sit on top */}
          <div
            className={cn(
              "order-2 grid w-full grid-cols-3 items-center justify-items-center gap-4",
              "md:order-none md:flex md:flex-1 md:w-auto md:justify-around md:gap-4",
              "lg:gap-[22px]"
            )}
          >
            {/* TODO: replace with real Porsche logo SVG */}
            <TrustLogoTile>
              <span className="whitespace-nowrap text-[13px] font-normal uppercase tracking-[0.26em] md:text-[12px]">
                Porsche
              </span>
            </TrustLogoTile>

            {/* TODO: replace with real U.S. Government seal SVG */}
            <TrustLogoTile>
              <span className="flex items-center gap-[5px]">
                <span
                  className="text-[18px] leading-none md:text-[16px]"
                  aria-hidden="true"
                >
                  ★
                </span>
                <span className="flex flex-col font-serif font-bold leading-[1.05] tracking-[0.06em]">
                  <span className="text-[12px] md:text-[11px]">U.S.</span>
                  <span className="text-[9px] tracking-[0.08em] md:text-[8px]">
                    GOVERNMENT
                  </span>
                </span>
              </span>
            </TrustLogoTile>

            {/* TODO: replace with real Miami University logo SVG */}
            <TrustLogoTile>
              <span className="flex items-center gap-[5px]">
                <span
                  className="font-serif text-[23px] font-bold leading-none md:text-[21px]"
                  aria-hidden="true"
                >
                  M
                </span>
                <span className="flex flex-col font-serif text-[10px] font-bold leading-[1.05] tracking-[0.04em] md:text-[9px]">
                  <span>MIAMI</span>
                  <span>UNIVERSITY</span>
                </span>
              </span>
            </TrustLogoTile>
          </div>

          {/* CENTER REVIEW BOX — order-1 on mobile so it sits on top */}
          <div
            className={cn(
              "order-1 flex flex-col items-center gap-[6px] rounded-[14px] border border-gray-100 bg-white text-center shrink-0 md:order-none md:rounded-[12px]",
              // Shadow per brief — soft elevated card lift
              "shadow-[0_12px_28px_-14px_rgba(11,14,16,0.14),0_1px_4px_-1px_rgba(11,14,16,0.06)]",
              // Sizing per brief: bigger on mobile so it doesn't get lost
              "min-w-[240px] px-[22px] py-[14px]",
              "md:min-w-[180px] md:px-[18px] md:py-[10px]"
            )}
          >
            {/* Excellent + 5 stars */}
            <div className="flex items-center justify-center gap-2">
              <h3 className="m-0 text-[13px] font-semibold leading-none text-semantic-text-primary md:text-[11px]">
                Excellent
              </h3>
              <div
                className="inline-flex gap-[1.5px]"
                aria-label={ratingLabel}
                role="img"
              >
                {Array.from({ length: 5 }).map((_, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center justify-center rounded-[2px] bg-[#00B67A] size-[20px] md:size-[16px]"
                  >
                    <TrustpilotStar className="size-[13px] text-white md:size-[10px]" />
                  </span>
                ))}
              </div>
            </div>

            {/* Rating line — "4.9 out of 5" */}
            <p className="m-0 text-[12px] leading-[1.3] text-gray-500 md:text-[10px]">
              <strong className="font-semibold text-semantic-text-primary">
                {TRUSTPILOT_RATING_DISPLAY} out of 5
              </strong>
            </p>

            {/* Dual-platform footer — Trustpilot + Google */}
            <div className="mt-[2px] inline-flex items-center border-t border-gray-100 pt-[8px] text-[11px] font-semibold text-semantic-text-primary md:pt-[6px] md:text-[10px]">
              <a
                href={TRUSTPILOT_REVIEW_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-[4px] transition-opacity hover:opacity-80"
                aria-label="View Languex reviews on Trustpilot"
              >
                <span className="inline-flex items-center justify-center rounded-[2px] bg-[#00B67A] size-[12px] md:size-[10px]">
                  <TrustpilotStar className="size-[7px] text-white md:size-[7px]" />
                </span>
                Trustpilot
              </a>

              <span
                aria-hidden="true"
                className="mx-[8px] inline-block h-[11px] w-px bg-neutral-200 md:mx-[6px] md:h-[9px]"
              />

              {GOOGLE_REVIEW_URL ? (
                <a
                  href={GOOGLE_REVIEW_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-[4px] transition-opacity hover:opacity-80"
                  aria-label="View Languex reviews on Google"
                >
                  <GoogleGlyph className="size-[12px] md:size-[10px]" />
                  Google
                </a>
              ) : (
                // GOOGLE_REVIEW_URL is nullable in constants.ts (still
                // populated today, but the type contract allows null).
                // Degrade to a non-link span so the platform mark still
                // shows up even if the URL gets unset later.
                <span className="inline-flex items-center gap-[4px]">
                  <GoogleGlyph className="size-[12px] md:size-[10px]" />
                  Google
                </span>
              )}
            </div>
          </div>

          {/* RIGHT WORDMARKS (3) — order-3 on mobile */}
          <div
            className={cn(
              "order-3 grid w-full grid-cols-3 items-center justify-items-center gap-4",
              "md:order-none md:flex md:flex-1 md:w-auto md:justify-around md:gap-4",
              "lg:gap-[22px]"
            )}
          >
            {/* TODO: replace with real Tastemade logo SVG */}
            <TrustLogoTile>
              <span className="whitespace-nowrap text-[13px] font-bold uppercase tracking-[0.14em] md:text-[12px]">
                Tastemade
              </span>
            </TrustLogoTile>

            {/* TODO: replace with real Mackenzie Health logo SVG */}
            <TrustLogoTile>
              <span className="whitespace-nowrap text-[12px] font-semibold uppercase tracking-[0.08em] md:text-[11px]">
                Mackenzie Health
              </span>
            </TrustLogoTile>

            {/* TODO: replace with real LAUSD shield + wordmark SVG */}
            <TrustLogoTile>
              <span className="flex items-center gap-[5px]">
                <span
                  aria-hidden="true"
                  className="h-[20px] w-[16px] shrink-0 bg-current md:h-[18px] md:w-[14px]"
                  style={{
                    clipPath:
                      "polygon(0 0, 100% 0, 100% 60%, 50% 100%, 0 60%)",
                  }}
                />
                <span className="flex flex-col font-bold leading-[1.05]">
                  <span className="text-[13px] tracking-[0.04em] md:text-[12px]">
                    LAUSD
                  </span>
                  <span className="text-[8px] font-semibold tracking-[0.10em] opacity-85 md:text-[7px]">
                    SCHOOL DISTRICT
                  </span>
                </span>
              </span>
            </TrustLogoTile>
          </div>
        </div>
      </div>
    </section>
  );
};

export default memo(TrustStrip);
